/**
 * Which server this build talks to, and which features without a server side yet are switched on.
 *
 *  mock - a fake server runs inside the page (src/mock). Nothing reaches Caryn's real server. Safe to share.
 *  live - Caryn's real server. Real fans, real messages, real money.
 *
 * TEST VERSIONS (owner, 2026-11-04: "add test versions"): features that have no server side yet. Owner 11-04 for the
 * real site: "everything connected to the API, no more demo places" - so in LIVE only the ones that fully work without
 * a server stay on (PIN on this device, last sign-in); nickname / gift show on the demo link only until their
 * server side exists (PLAN.md §5). Turning one on in live = change IS_MOCK to true below.
 */
export const API_MODE: 'mock' | 'live' = import.meta.env.VITE_API_MODE === 'live' ? 'live' : 'mock'
export const IS_MOCK = API_MODE === 'mock'
export const API_URL: string = import.meta.env.VITE_API_URL || 'http://localhost:3000'

/**
 * Home screen version (owner 2026-11-04: "back toward the old home, new styling, no sticky note - show me versions").
 *   familiar        - closest to today's home: one card, lowercase buttons, icon squares, mailbox "click me", pumpkin
 *   classic         - the old cutiecaryn.com layout in the new style: big Text / Gallery / Inbox buttons + 4 icon tiles
 *   classic-preview - classic, with the latest message + unread count on the Text button
 *   tiles           - the original Texts + Gallery cards, then one row of equal buttons (Inbox, Vault, Diary, Wallet, Account)
 *   design          - the designer's v7 home (texts card, gallery card, vault bar), sticky note removed
 * Anyone can try another with ?home=<id> (remembered in this browser). Change the default after the owner picks.
 */
export const HOME_VARIANTS = ['familiar', 'classic', 'classic-preview', 'tiles', 'design'] as const
export type HomeVariantId = (typeof HOME_VARIANTS)[number]
export const HOME_VARIANT: HomeVariantId = 'classic-preview' // owner 2026-11-04: "loving version 3"

export const FEATURES = {
  /** 4-digit PIN before Text and the Vault - test version: created by the fan, hash kept on this device (src/app/pin.tsx). */
  pinGate: true,
  /** "What should Caryn call you?" - kept on this device; Caryn's team does not see it yet -> demo only until saved on the server. */
  nickname: IS_MOCK,
  /** "Send gift" in the + menu - would send a free gift note as a normal text (no payment) -> demo only until gifts are real. */
  gift: IS_MOCK,
  /** Diary - entries from Caryn's server (GET /api/messages/diary; for now one welcome entry). */
  diary: true,
  /** "Last sign-in" line - test version: the previous sign-in on this device. */
  lastSignIn: true,
  /** Hearts on messages (the only reaction in v2). GO-LIVE PLAN (owner 11-04): first push with this false + reactions
   *  disabled on the backend and admin, test live, then turn hearts back on (admin hearts only). See HANDOFF.md §0. */
  hearts: true, // owner 11-04 go-live: hearts only - the admin can only send hearts and the backend refuses any other reaction
  /** "You've got mail" mailing-address prompt on Home - OFF for now (owner 2026-11-04). true brings it back. */
  mailboxPrompt: false,
  /** Gallery photo comments - nothing exists; demo shows the design's preview only. */
  photoComments: IS_MOCK,
}
